import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Contact, ContactListResponse, CreateContactRequest, UpdateContactRequest } from '@df-gate-share/shared';
import { apiClient, getServerErrorMessage } from '../lib/api';
import Header from '../components/Header';

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'organization' | 'createdAt' | 'interactionCount'>('interactionCount');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [currentCursor, setCurrentCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateContactRequest>({
    name: '',
    email: '',
    organization: '',
    memo: ''
  });

  // Dialog error
  const [dialogError, setDialogError] = useState<string>('');

  // Toast message
  const [toastMessage, setToastMessage] = useState<string>('');

  // Initial fetch
  useEffect(() => {
    fetchContacts(undefined, true);
  }, []);

  // Debounce search
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchContacts(undefined, true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchQuery, sortBy, sortOrder]);

  const fetchContacts = async (cursor?: string, resetHistory: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (searchQuery) params.append('search', searchQuery);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      if (cursor) params.append('cursor', cursor);

      const response = await apiClient.get(`/api/v1/contacts?${params.toString()}`);
      const data: ContactListResponse = response.data;

      setContacts(data.data || []);
      setNextCursor(data.meta.nextCursor);
      setHasMore(data.meta.hasMore);

      if (resetHistory) {
        setCursorHistory([]);
        setCurrentCursor(undefined);
      } else {
        setCurrentCursor(cursor);
      }
    } catch (err) {
      setError(getServerErrorMessage(err, '連絡先の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (nextCursor && hasMore) {
      setCursorHistory([...cursorHistory, currentCursor]);
      fetchContacts(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    const previousCursor = cursorHistory[cursorHistory.length - 1];
    setCursorHistory(cursorHistory.slice(0, -1));
    fetchContacts(previousCursor);
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogError('');

    try {
      await apiClient.post('/api/v1/contacts', formData);
      setToastMessage('連絡先を追加しました');
      setShowAddDialog(false);
      setDialogError('');
      setFormData({ name: '', email: '', organization: '', memo: '' });
      fetchContacts(undefined, true);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (err) {
      setDialogError(getServerErrorMessage(err, '連絡先の追加に失敗しました'));
    }
  };

  const handleEditContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;
    setDialogError('');

    try {
      const updateData: UpdateContactRequest = {
        name: formData.name,
        organization: formData.organization,
        memo: formData.memo
      };
      await apiClient.put(`/api/v1/contacts/${selectedContact.contactId}`, updateData);
      setToastMessage('連絡先を更新しました');
      setShowEditDialog(false);
      setDialogError('');
      setSelectedContact(null);
      setFormData({ name: '', email: '', organization: '', memo: '' });
      fetchContacts(currentCursor);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (err) {
      setDialogError(getServerErrorMessage(err, '連絡先の更新に失敗しました'));
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    setDialogError('');

    try {
      await apiClient.delete(`/api/v1/contacts/${selectedContact.contactId}`);
      setToastMessage('連絡先を削除しました');
      setShowDeleteDialog(false);
      setDialogError('');
      setSelectedContact(null);
      fetchContacts(currentCursor);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (err) {
      setDialogError(getServerErrorMessage(err, '連絡先の削除に失敗しました'));
    }
  };

  const openAddDialog = () => {
    setFormData({ name: '', email: '', organization: '', memo: '' });
    setDialogError('');
    setShowAddDialog(true);
  };

  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      organization: contact.organization || '',
      memo: contact.memo || ''
    });
    setDialogError('');
    setShowEditDialog(true);
  };

  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setDialogError('');
    setShowDeleteDialog(true);
  };

  return (
    <div className="page-wrapper">
      <Header />

      <div className="page-container">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">連絡先管理</h1>
          <button className="btn btn--primary" onClick={openAddDialog}>
            連絡先を追加
          </button>
        </div>

        {/* Search and Sort Controls */}
        <div className="filter-bar">
          <input
            type="text"
            className="form-input"
            placeholder="名前、組織名、メールアドレスで検索"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />

          <div className="filter-group">
            <label className="filter-label">
              並び替え:
            </label>
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{ minWidth: '140px' }}
            >
              <option value="interactionCount">やり取り回数</option>
              <option value="name">名前</option>
              <option value="organization">組織名</option>
              <option value="createdAt">登録日時</option>
            </select>
            <select
              className="form-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as any)}
            >
              <option value="desc">降順</option>
              <option value="asc">昇順</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert--error">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center text-muted" style={{ padding: '40px' }}>
            読み込み中...
          </div>
        )}

        {/* Empty State */}
        {!loading && contacts.length === 0 && (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state__icon"><span className="material-symbols-outlined icon-xl">contacts</span></div>
              <div className="empty-state__title">
                連絡先がまだ登録されていません
              </div>
              <button className="btn btn--primary mt-6" onClick={openAddDialog}>
                連絡先を追加
              </button>
            </div>
          </div>
        )}

        {/* Contacts Table */}
        {!loading && contacts.length > 0 && (
          <>
            <div className="card">
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>名前</th>
                      <th>組織名</th>
                      <th>メールアドレス</th>
                      <th>やり取り回数</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.contactId}>
                        <td>{contact.name}</td>
                        <td>{contact.organization || '-'}</td>
                        <td>{contact.email}</td>
                        <td>{contact.interactionCount}回</td>
                        <td>
                          <div className="flex gap-2">
                            <button
                              className="btn btn--sm btn--secondary"
                              onClick={() => openEditDialog(contact)}
                            >
                              編集
                            </button>
                            <button
                              className="btn btn--sm btn--outline-danger"
                              onClick={() => openDeleteDialog(contact)}
                            >
                              削除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="pagination justify-center">
              <div className="pagination__buttons">
                {cursorHistory.length > 0 && (
                  <button className="btn btn--secondary" onClick={handlePreviousPage}>
                    前へ
                  </button>
                )}
                {hasMore && (
                  <button className="btn btn--secondary" onClick={handleNextPage}>
                    次へ
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Dialog */}
      {showAddDialog && (
        <div className="modal-overlay">
          <div className="modal" role="dialog">
            <h2 className="modal__title">連絡先の追加</h2>
            {dialogError && (
              <div className="alert alert--error">
                {dialogError}
              </div>
            )}
            <form onSubmit={handleAddContact}>
              <div className="form-group">
                <label htmlFor="add-name" className="form-label">
                  名前 *
                </label>
                <input
                  id="add-name"
                  type="text"
                  className="form-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="add-email" className="form-label">
                  メールアドレス *
                </label>
                <input
                  id="add-email"
                  type="email"
                  className="form-input"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="add-organization" className="form-label">
                  組織名
                </label>
                <input
                  id="add-organization"
                  type="text"
                  className="form-input"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="add-memo" className="form-label">
                  メモ
                </label>
                <textarea
                  id="add-memo"
                  className="form-textarea"
                  value={formData.memo}
                  onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowAddDialog(false)}
                >
                  キャンセル
                </button>
                <button type="submit" className="btn btn--primary">
                  追加
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      {showEditDialog && selectedContact && (
        <div className="modal-overlay">
          <div className="modal" role="dialog">
            <h2 className="modal__title">連絡先の編集</h2>
            {dialogError && (
              <div className="alert alert--error">
                {dialogError}
              </div>
            )}
            <form onSubmit={handleEditContact}>
              <div className="form-group">
                <label htmlFor="edit-name" className="form-label">
                  名前 *
                </label>
                <input
                  id="edit-name"
                  type="text"
                  className="form-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-email" className="form-label">
                  メールアドレス
                </label>
                <input
                  id="edit-email"
                  type="email"
                  className="form-input"
                  disabled
                  value={formData.email}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-organization" className="form-label">
                  組織名
                </label>
                <input
                  id="edit-organization"
                  type="text"
                  className="form-input"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-memo" className="form-label">
                  メモ
                </label>
                <textarea
                  id="edit-memo"
                  className="form-textarea"
                  value={formData.memo}
                  onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setShowEditDialog(false)}
                >
                  キャンセル
                </button>
                <button type="submit" className="btn btn--primary">
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {showDeleteDialog && selectedContact && (
        <div className="modal-overlay">
          <div className="modal" role="dialog">
            <h2 className="modal__title">連絡先の削除</h2>
            {dialogError && (
              <div className="alert alert--error">
                {dialogError}
              </div>
            )}
            <p className="text-muted mb-6">
              この連絡先を削除しますか？
            </p>
            <p className="mb-6 text-sm">
              <strong>{selectedContact.name}</strong> ({selectedContact.email})
            </p>
            <div className="modal__actions">
              <button
                className="btn btn--secondary"
                onClick={() => setShowDeleteDialog(false)}
              >
                キャンセル
              </button>
              <button
                className="btn btn--danger"
                onClick={handleDeleteContact}
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="toast">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default Contacts;
